import { useState, useCallback } from 'react';

/**
 * Available batch operation modal types
 */
export type BatchOperationModalType = 'date' | 'amount' | 'account' | 'category';

/**
 * Modal state record type
 */
export type BatchOperationModalsState = Record<BatchOperationModalType, boolean>;

/**
 * Return type for useBatchOperationModals hook
 */
export interface UseBatchOperationModalsReturn {
  /** Current state of all modals */
  modals: BatchOperationModalsState;
  /** Open a specific modal */
  openModal: (modal: BatchOperationModalType) => void;
  /** Close a specific modal */
  closeModal: (modal: BatchOperationModalType) => void;
  /** Toggle a specific modal */
  toggleModal: (modal: BatchOperationModalType) => void;
  /** Close all modals */
  closeAllModals: () => void;
  /** Check if any modal is open */
  isAnyModalOpen: boolean;
}

/**
 * Custom hook for managing batch operation modal states
 *
 * Consolidates modal state management for batch operations on transactions.
 * Used across AccrualPage, ReceivablesPage, PayablesPage for consistency.
 *
 * @example
 * ```tsx
 * const { modals, openModal, closeModal } = useBatchOperationModals();
 *
 * <Button onClick={() => openModal('date')}>Update Dates</Button>
 * <BatchDateModal
 *   isOpen={modals.date}
 *   onClose={() => closeModal('date')}
 * />
 * ```
 */
export function useBatchOperationModals(): UseBatchOperationModalsReturn {
  const [modals, setModals] = useState<BatchOperationModalsState>({
    date: false,
    amount: false,
    account: false,
    category: false,
  });

  /**
   * Open a specific modal
   */
  const openModal = useCallback((modal: BatchOperationModalType) => {
    setModals((prev) => ({ ...prev, [modal]: true }));
  }, []);

  /**
   * Close a specific modal
   */
  const closeModal = useCallback((modal: BatchOperationModalType) => {
    setModals((prev) => ({ ...prev, [modal]: false }));
  }, []);

  /**
   * Toggle a specific modal (open if closed, close if open)
   */
  const toggleModal = useCallback((modal: BatchOperationModalType) => {
    setModals((prev) => ({ ...prev, [modal]: !prev[modal] }));
  }, []);

  /**
   * Close all modals at once
   */
  const closeAllModals = useCallback(() => {
    setModals({
      date: false,
      amount: false,
      account: false,
      category: false,
    });
  }, []);

  /**
   * Check if any modal is currently open
   */
  const isAnyModalOpen = Object.values(modals).some((isOpen) => isOpen);

  return {
    modals,
    openModal,
    closeModal,
    toggleModal,
    closeAllModals,
    isAnyModalOpen,
  };
}
